// src/features/admin-templates/components/TaskTemplatesPanel.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Refresh as RefreshIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { useTaskTemplates } from '../../tasks/hooks/useTaskTemplates';
import { useTemplateAdminPermissions } from '../../auth/hooks/usePermissions';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { useConfirmDialog } from '../../../hooks/mui/useConfirmDialog';

const UNITS = ['days', 'weeks', 'months', 'years'];

const EMPTY_FORM = {
  name: '',
  description: '',
  category: '',
  is_recurring: true,
  recurrence_rule: '',
  frequency_interval: 1,
  unit: 'weeks',
  skip_closing_days: false,
  law_requirement: false,
  version: 1,
};

function TaskTemplateFormFields({ f, onChange, errors, t }) {
  return (
    <Box display="flex" flexDirection="column" gap={2} pt={1}>
      <Box display="flex" gap={2}>
        <TextField
          label={t('tasks:labels.name')}
          value={f.name}
          onChange={e => onChange('name', e.target.value)}
          error={!!errors.name}
          helperText={errors.name}
          size="small"
          sx={{ flex: 2 }}
        />
        <TextField
          label={t('tasks:labels.category')}
          value={f.category}
          onChange={e => onChange('category', e.target.value)}
          error={!!errors.category}
          helperText={errors.category}
          size="small"
          sx={{ flex: 1 }}
        />
      </Box>

      <TextField
        fullWidth
        multiline
        rows={6}
        label={t('common:labels.description')}
        value={f.description}
        onChange={e => onChange('description', e.target.value)}
        error={!!errors.description}
        helperText={errors.description}
      />

      <TextField
        fullWidth
        label={t('tasks:labels.recurrence_rule')}
        value={f.recurrence_rule}
        onChange={e => onChange('recurrence_rule', e.target.value)}
        error={!!errors.recurrence_rule}
        helperText={errors.recurrence_rule || 'e.g. FREQ=WEEKLY;BYDAY=MO'}
        size="small"
      />

      <Box display="flex" gap={2} alignItems="flex-start" flexWrap="wrap">
        <TextField
          type="number"
          label={t('tasks:catalog.dialog.frequency')}
          value={f.frequency_interval}
          onChange={e => onChange('frequency_interval', e.target.value)}
          size="small"
          inputProps={{ min: 1 }}
          sx={{ width: 110 }}
        />
        <Select
          value={f.unit}
          onChange={e => onChange('unit', e.target.value)}
          size="small"
          sx={{ width: 120 }}
        >
          {UNITS.map(u => <MenuItem key={u} value={u}>{t(`tasks:catalog.frequency.units.${u}`, u)}</MenuItem>)}
        </Select>
        <TextField
          type="number"
          label={t('tasks:labels.version')}
          value={f.version}
          onChange={e => onChange('version', e.target.value)}
          size="small"
          inputProps={{ min: 1 }}
          sx={{ width: 90 }}
        />
        <Box display="flex" alignItems="center" flexWrap="wrap" gap={1} ml="auto">
          <FormControlLabel
            control={<Switch checked={f.is_recurring} onChange={e => onChange('is_recurring', e.target.checked)} />}
            label={t('tasks:labels.recurring')}
          />
          <FormControlLabel
            control={<Switch checked={f.law_requirement} onChange={e => onChange('law_requirement', e.target.checked)} />}
            label={t('tasks:labels.law_requirement')}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default function TaskTemplatesPanel() {
  const { t } = useTranslation(['tasks', 'common']);
  const { showSuccess, showError } = useSnackbar();
  const { showConfirmDialog, ConfirmDialog } = useConfirmDialog();
  const { canCreate, canUpdate, canDelete } = useTemplateAdminPermissions();

  const {
    templates,
    loading,
    error,
    clearError,
    fetchAllTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,
  } = useTaskTemplates();

  const [createOpen, setCreateOpen] = useState(false);
  const [editState, setEditState] = useState({ open: false, templateId: null });
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    fetchAllTemplates();
  }, [fetchAllTemplates]);

  const openCreate = useCallback(() => {
    setForm(EMPTY_FORM);
    setFormErrors({});
    setCreateOpen(true);
  }, []);

  const openEdit = useCallback((row) => {
    setForm({
      name: row.name ?? '',
      description: row.description ?? '',
      category: row.category ?? '',
      is_recurring: row.is_recurring ?? true,
      recurrence_rule: row.recurrence_rule ?? '',
      frequency_interval: row.frequency_interval ?? 1,
      unit: row.unit ?? 'weeks',
      skip_closing_days: row.skip_closing_days ?? false,
      law_requirement: row.law_requirement ?? false,
      version: row.version ?? 1,
    });
    setFormErrors({});
    setEditState({ open: true, templateId: row.template_id });
  }, []);

  const closeAll = useCallback(() => {
    setCreateOpen(false);
    setEditState({ open: false, templateId: null });
    setForm(EMPTY_FORM);
    setFormErrors({});
  }, []);

  const handleFormChange = useCallback((field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setFormErrors(prev => ({ ...prev, [field]: null }));
  }, []);

  const validate = (f) => {
    const errors = {};
    if (!f.name.trim()) errors.name = t('common:validation.required');
    if (!f.description.trim()) errors.description = t('common:validation.required');
    if (!f.category.trim()) errors.category = t('common:validation.required');
    if (!f.recurrence_rule.trim()) errors.recurrence_rule = t('common:validation.required');
    return errors;
  };

  const buildPayload = (f) => ({
    name: f.name.trim(),
    description: f.description.trim(),
    category: f.category.trim(),
    is_recurring: f.is_recurring,
    recurrence_rule: f.recurrence_rule.trim(),
    frequency_interval: parseInt(f.frequency_interval, 10),
    unit: f.unit,
    skip_closing_days: f.skip_closing_days,
    law_requirement: f.law_requirement,
    version: parseInt(f.version, 10) || 1,
  });

  const handleCreate = async () => {
    const errors = validate(form);
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }
    const result = await createTemplate(buildPayload(form));
    if (result.success) {
      showSuccess(t('tasks:admin.created'));
      closeAll();
    } else {
      showError(result.error);
    }
  };

  const handleUpdate = async () => {
    const errors = validate(form);
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }
    const result = await updateTemplate(editState.templateId, buildPayload(form));
    if (result.success) {
      showSuccess(t('tasks:admin.updated'));
      closeAll();
    } else {
      showError(result.error);
    }
  };

  const handleDelete = useCallback((row) => {
    showConfirmDialog({
      title: t('tasks:admin.confirm_delete_title'),
      message: t('tasks:admin.confirm_delete_message', { name: row.name }),
      severity: 'error',
      onConfirm: async () => {
        const result = await deleteTemplate(row.template_id);
        if (result.success) {
          showSuccess(t('tasks:admin.deleted'));
        } else {
          showError(result.error);
        }
      },
    });
  }, [showConfirmDialog, deleteTemplate, showSuccess, showError, t]);

  const columns = [
    { field: 'name', headerName: t('tasks:labels.name'), flex: 2, minWidth: 160 },
    { field: 'category', headerName: t('tasks:labels.category'), width: 140 },
    {
      field: 'frequency_interval',
      headerName: t('tasks:catalog.dialog.frequency'),
      width: 110,
      renderCell: ({ row }) => `${row.frequency_interval} ${row.unit}`,
    },
    {
      field: 'is_recurring',
      headerName: t('tasks:labels.recurring'),
      width: 110,
      renderCell: ({ row }) => (
        <Chip
          label={row.is_recurring ? t('common:labels.yes') : t('common:labels.no')}
          size="small"
          color={row.is_recurring ? 'success' : 'default'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'law_requirement',
      headerName: t('tasks:labels.law_requirement'),
      width: 120,
      renderCell: ({ row }) =>
        row.law_requirement ? <Chip label={t('common:labels.yes')} size="small" color="warning" /> : '—',
    },
    { field: 'version', headerName: t('tasks:labels.version'), width: 80, type: 'number' },
    {
      field: 'actions',
      headerName: '',
      width: 90,
      sortable: false,
      renderCell: ({ row }) => (
        <Box display="flex" gap={0.5}>
          {canUpdate && (
            <Button size="small" onClick={() => openEdit(row)} sx={{ minWidth: 0 }}>
              <EditIcon fontSize="small" />
            </Button>
          )}
          {canDelete && (
            <Button size="small" color="error" onClick={() => handleDelete(row)} sx={{ minWidth: 0 }}>
              <DeleteIcon fontSize="small" />
            </Button>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary" paragraph>
          {t('tasks:admin.description')}
        </Typography>

        <Box mb={2} display="flex" gap={1}>
          {canCreate && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              {t('tasks:admin.add_button')}
            </Button>
          )}
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchAllTemplates}>
            {t('common:buttons.refresh')}
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>{error}</Alert>}

        <DataGrid
          rows={templates}
          columns={columns}
          getRowId={row => row.template_id}
          loading={loading}
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          autoHeight
          disableRowSelectionOnClick
          sx={{ border: 0 }}
        />
      </Paper>

      <Dialog open={createOpen} onClose={closeAll} maxWidth="sm" fullWidth>
        <DialogTitle>{t('tasks:admin.new_template')}</DialogTitle>
        <DialogContent>
          <TaskTemplateFormFields f={form} onChange={handleFormChange} errors={formErrors} t={t} />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAll}>{t('common:buttons.cancel')}</Button>
          <Button variant="contained" onClick={handleCreate} disabled={loading}>{t('common:buttons.save')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editState.open} onClose={closeAll} maxWidth="sm" fullWidth>
        <DialogTitle>{t('tasks:admin.edit_template')}</DialogTitle>
        <DialogContent>
          <TaskTemplateFormFields f={form} onChange={handleFormChange} errors={formErrors} t={t} />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAll}>{t('common:buttons.cancel')}</Button>
          <Button variant="contained" onClick={handleUpdate} disabled={loading}>{t('common:buttons.save')}</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog />
    </Box>
  );
}
